/**
 * Kaufzonen-Balken — auf einen Blick über die ganze Watchlist sehen, welche
 * Werte sich der Zone nähern, ohne jede Detailseite zu öffnen (23.09.2026,
 * Ronni: „schnelle Übersicht über die Lage der Aktie").
 *
 * Zeigt NUR bei `zone_offen` — sonst gibt es kein Band zu zeichnen, und
 * „nichts zu tun" bleibt der unauffällige Normalfall (kein leerer Balken,
 * der wie ein Fehler aussieht).
 *
 * KEIN ZUSÄTZLICHER FETCH: `useZoneState` liest denselben Snapshot-Cache
 * wie `ZoneStateHeadline` (TanStack-Query-Dedup über denselben Query-Key),
 * `currentPrice` kommt vom ohnehin schon pro Zeile laufenden Kursabruf in
 * `SymbolQuoteCell` — 76 Zeilen bedeuten hier keine 76 neuen Abfragen.
 */
import { useZoneState } from '@/hooks/use-zone-state';
import { cn } from '@/lib/utils';

interface Props {
  symbol: string;
  assetClass?: string;
  currentPrice: number | null;
}

export function KaufzonenBalken({ symbol, assetClass, currentPrice }: Props) {
  const { state, applicable } = useZoneState(symbol, assetClass);

  if (!applicable || !state || state.zustand !== 'zone_offen' || currentPrice === null) {
    return null;
  }
  const { limit_bis, ungueltig_unter } = state;
  if (limit_bis === null || ungueltig_unter === null) return null;
  const breite = limit_bis - ungueltig_unter;
  if (!(breite > 0)) return null;

  // 0 % = Stop, 100 % = Limit — auf ±15 % geklemmt, damit der Punkt bei
  // knapp außerhalb liegendem Kurs noch im sichtbaren Balken bleibt.
  const rohPos = (currentPrice - ungueltig_unter) / breite;
  const pos = Math.min(1.15, Math.max(-0.15, rohPos));
  const imBand = currentPrice >= ungueltig_unter && currentPrice <= limit_bis;
  const darueber = currentPrice > limit_bis;

  return (
    <div
      className="mt-1.5 flex items-center gap-1.5"
      title={`Zone ${ungueltig_unter.toFixed(2)}–${limit_bis.toFixed(2)}, Kurs ${currentPrice.toFixed(2)}`}
    >
      <div className="relative h-1.5 w-16 rounded-full bg-zinc-800">
        <div
          className={cn(
            'absolute inset-y-0 rounded-full',
            imBand ? 'bg-emerald-500/60' : darueber ? 'bg-zinc-600' : 'bg-zinc-700'
          )}
          style={{ left: 0, right: 0 }}
        />
        <div
          className={cn(
            'absolute top-1/2 size-2 -translate-y-1/2 -translate-x-1/2 rounded-full border border-zinc-950',
            imBand ? 'bg-emerald-300' : darueber ? 'bg-zinc-400' : 'bg-rose-400'
          )}
          style={{ left: `${pos * 100}%` }}
        />
      </div>
      <span className="text-[9px] text-zinc-600">
        {imBand ? 'im Band' : darueber ? 'darüber' : 'darunter'}
      </span>
    </div>
  );
}
